import { v4 as uuid } from 'uuid';
import { Plus, Trash2, CheckCircle } from 'lucide-react';
import { useEditorStore } from '../../store/editorStore';

export default function InteractiveProperties({ layer }) {
  const data = layer.data;
  const updateLayerData = useEditorStore((s) => s.updateLayerData);

  function update(updates) {
    updateLayerData(layer.id, updates);
  }

  const options = (data.options || []);

  function updateOption(optId, text) {
    update({ options: options.map((o) => o.id === optId ? { ...o, text } : o) });
  }

  function addOption() {
    update({ options: [...options, { id: uuid(), text: `Option ${options.length + 1}`, isCorrect: false }] });
  }

  function removeOption(optId) {
    update({ options: options.filter((o) => o.id !== optId) });
  }

  function toggleCorrect(optId) {
    update({
      options: options.map((o) => ({
        ...o,
        isCorrect: data.interactiveType?.includes('quiz') ? o.id === optId : o.isCorrect,
      })),
    });
  }

  const isQuiz = data.interactiveType?.includes('quiz');
  const isSlider = data.interactiveType === 'slider';

  return (
    <div className="p-2 space-y-3">
      <p className="panel-label">Interactive — {data.interactiveType?.replace('-', ' ')}</p>

      <div>
        <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Prompt Text</label>
        <input
          type="text" value={data.prompt || ''}
          onChange={(e) => update({ prompt: e.target.value })}
          className="input-field text-xs"
          placeholder="Ask your question..."
        />
      </div>

      <div>
        <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Theme</label>
        <select value={data.theme || 'dark'} onChange={(e) => update({ theme: e.target.value })}
                className="input-field text-xs">
          <option value="dark">Dark</option>
          <option value="light">Light</option>
        </select>
      </div>

      {isSlider && (
        <div className="space-y-2">
          <p className="text-xs font-medium" style={{ color: 'var(--t-muted)' }}>Slider Settings</p>
          <div className="grid grid-cols-3 gap-1">
            {['sliderMin', 'sliderMax', 'sliderInitialValue'].map((f) => (
              <div key={f}>
                <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>
                  {f === 'sliderMin' ? 'Min' : f === 'sliderMax' ? 'Max' : 'Initial'}
                </label>
                <input
                  type="number"
                  value={data[f] ?? (f === 'sliderMax' ? 100 : f === 'sliderInitialValue' ? 50 : 0)}
                  onChange={(e) => update({ [f]: parseInt(e.target.value) })}
                  className="input-field text-xs py-1"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {!isSlider && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium" style={{ color: 'var(--t-muted)' }}>
              Options {isQuiz && <span style={{ color: 'var(--t-muted)' }}>(mark correct)</span>}
            </label>
            <button onClick={addOption} className="icon-btn w-6 h-6">
              <Plus size={12} />
            </button>
          </div>
          <div className="space-y-1.5">
            {options.map((opt, i) => (
              <div key={opt.id} className="flex items-center gap-1.5">
                {isQuiz && (
                  <button onClick={() => toggleCorrect(opt.id)} className="flex-shrink-0">
                    <CheckCircle
                      size={14}
                      className={opt.isCorrect ? 'text-green-500' : ''}
                      style={!opt.isCorrect ? { color: 'var(--t-muted)' } : {}}
                    />
                  </button>
                )}
                <span className="text-xs w-4 flex-shrink-0" style={{ color: 'var(--t-muted)' }}>{String.fromCharCode(65 + i)}.</span>
                <input
                  type="text"
                  value={opt.text}
                  onChange={(e) => updateOption(opt.id, e.target.value)}
                  className="input-field text-xs py-1 flex-1 min-w-0"
                  placeholder={`Option ${i + 1}`}
                />
                <button onClick={() => removeOption(opt.id)} className="flex-shrink-0 hover:text-red-400" style={{ color: 'var(--t-muted)' }}>
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Backend Endpoint URL</label>
        <input
          type="url"
          value={data.endpointUrl || ''}
          onChange={(e) => update({ endpointUrl: e.target.value })}
          className="input-field text-xs"
          placeholder="https://your-api.example.com/vote"
        />
        <p className="text-xs mt-1" style={{ color: 'var(--t-muted)' }}>
          AMP will POST vote data here. Uses backend ID: {data.interactionId || 'not set'}
        </p>
      </div>
    </div>
  );
}
